<template>
  <b-card v-if="hasAudio || isOwner" class="py-4">
    <div class="flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <Icon name="solar:microphone-3-bold-duotone" size="1.5rem" class="text-white" />
          <span class="text-white text-lg font-medium">
            {{ t('profile_audio_demo.title') }}
          </span>
        </div>
        <b-button v-if="isOwner && hasAudio" light size="sm" @click="confirmDeleteAudio = true">
          <Icon name="solar:trash-bin-trash-bold-duotone" size="1rem" class="text-red-400" />
        </b-button>
      </div>

      <div v-if="hasAudio" class="px-1">
        <audio
          controls
          preload="metadata"
          :src="audioUrl"
          class="w-full h-10 audio-player"
        />
      </div>

      <div v-else-if="isOwner" class="flex flex-col items-center gap-2">
        <b-button light size="sm" @click="triggerUpload" :loading="isUploading">
          <Icon name="solar:upload-bold-duotone" size="1rem" class="mr-2" />
          {{ t('profile_audio_demo.add') }}
        </b-button>
        <p class="text-xs text-gray-400 text-center">{{ t('profile_audio_demo.constraints') }}</p>
      </div>
    </div>
  </b-card>

  <input
    ref="audioFileInput"
    type="file"
    hidden
    accept="audio/mpeg,audio/mp4,audio/x-m4a,.mp3,.m4a"
    @change="onFileSelected"
  />

  <b-modal v-model="confirmDeleteAudio" :title="t('profile_audio_demo.delete')">
    <template #default>
      <p class="px-10 pb-6 text-gray-300">{{ t('profile_audio_demo.delete_confirm') }}</p>
    </template>
    <template #footer>
      <div class="flex justify-end gap-3 px-10 pb-8">
        <b-button light @click="confirmDeleteAudio = false">{{ t('common.cancel') }}</b-button>
        <b-button @click="onDeleteAudio" :loading="isDeleting">{{ t('profile_audio_demo.delete') }}</b-button>
      </div>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import BCard from './BCard.vue';
import { authStore } from '~/stores/auth';
import { useProfileStore } from '~/stores/profileStore';
import { useUserStore } from '~/stores/userStore';
import BButton from '~/components/forms/BButton.vue';
import BModal from '~/components/modal/BModal.vue';
import { useAlerts } from '~/stores/alert';

const { t } = useI18n();
const alerts = useAlerts();

const store = authStore();
const profileStore = useProfileStore();
const userStore = useUserStore();
const { user } = storeToRefs(userStore);
const { user: me } = storeToRefs(store);

const { isPreview } = useProfilePreviewMode();
const isOwner = computed(() => !isPreview.value && me.value && user.value && me.value.id === user.value.id);

const audioFileInput = ref<HTMLInputElement>();
const audioUrl = computed(() => user.value?.profile?.signed_audio_demo_url || '');
const hasAudio = computed(() => !!audioUrl.value);
const isUploading = ref(false);
const isDeleting = ref(false);
const confirmDeleteAudio = ref(false);

const MAX_FILE_SIZE = 6 * 1024 * 1024;
const MAX_DURATION = 300;

function triggerUpload() {
  audioFileInput.value?.click();
}

function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(audio.src);
      resolve(audio.duration);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(audio.src);
      reject(new Error('Failed to load audio metadata'));
    };
    audio.src = URL.createObjectURL(file);
  });
}

async function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  input.value = '';

  const allowedTypes = ['audio/mpeg', 'audio/mp4', 'audio/x-m4a'];
  if (!allowedTypes.includes(file.type)) {
    alerts.add({ type: 'danger', title: t('profile_audio_demo.format_error') });
    return;
  }

  if (file.size > MAX_FILE_SIZE) {
    alerts.add({ type: 'danger', title: t('profile_audio_demo.size_error') });
    return;
  }

  try {
    const duration = await getAudioDuration(file);
    if (duration > MAX_DURATION) {
      alerts.add({ type: 'danger', title: t('profile_audio_demo.duration_error') });
      return;
    }
  } catch {
    // Browser could not read audio metadata (unsupported codec or sandbox restriction).
    // Proceed — server enforces 3MB hard limit which implicitly caps duration.
  }

  isUploading.value = true;
  try {
    await profileStore.uploadAudioDemo(user.value!.id, file);

    const slug = user.value?.profile?.slug;
    if (slug) {
      const $api = useNuxtApp().$api();
      const refreshed = await $api.v2.users.get(slug);
      userStore.user = refreshed;
    }

    alerts.add({ type: 'success', title: t('profile_audio_demo.upload_success') });
  } catch {
    alerts.add({ type: 'danger', title: t('profile_audio_demo.upload_error') });
  } finally {
    isUploading.value = false;
  }
}

async function onDeleteAudio() {
  isDeleting.value = true;
  try {
    await profileStore.deleteAudioDemo(user.value!.id);
    if (user.value?.profile) {
      (user.value.profile as any).signed_audio_demo_url = null;
    }
    alerts.add({ type: 'success', title: t('profile_audio_demo.delete_success') });
  } catch {
    alerts.add({ type: 'danger', title: t('profile_audio_demo.delete_error') });
  } finally {
    isDeleting.value = false;
    confirmDeleteAudio.value = false;
  }
}
</script>

<style scoped>
.audio-player {
  filter: invert(1) hue-rotate(180deg);
  border-radius: 8px;
}
</style>
